import { feeUsd } from '@/features/home/lib/home-trade-stats'
import { isUsdLikeCurrency } from '@/lib/fee-format'
import { isInverseInstrument } from '@/lib/instrument-util'
import { OrderSide, type Trade } from '@/types/generated/common/trading'
import { type Exchange, type Instrument, InstrumentKind } from '@/types/generated/common/types'

/**
 * "Your trading, explained": the user's fills paired into complete trades (opened from flat,
 * closed back to flat), then the numbers a coach would look at — how often they're right, how
 * big a typical win is against a typical loss, what paid and what leaked (by side, hold time,
 * session, instrument, day, and right after a loss) — and plain rules drawn from them.
 *
 * Pairing walks each account/instrument's fills in time. The position at the window start is
 * the current position minus the window's net fills; a trade already open then (its entry is
 * outside the window) is skipped, as are trades still open now. USD-quoted linear and inverse
 * contracts only; options are left out. Pure (tested in __tests__/home-trade-journal.test.ts).
 */

export type TradeSide = 'long' | 'short'
export type HoldBucket = 'lt5m' | 'm5to15' | 'm15to60' | 'h1to4' | 'gt4h'
/** Trading sessions in UTC: Asia 00–08, Europe 08–13, US 13–21, late US 21–24. */
export type Session = 'asia' | 'europe' | 'us' | 'late'

export interface RoundTrip {
  id: string
  accountId: string
  exchange: Exchange
  symbol: string
  side: TradeSide
  openedAt: number
  closedAt: number
  holdMs: number
  entryPrice: number
  exitPrice: number
  /** The largest size held, in USD at entry. */
  notionalUsd: number
  grossPnlUsd: number
  feesUsd: number
  /** After fees. */
  pnlUsd: number
  fills: number
}

export interface JournalSlice<K extends string = string> {
  key: K
  trips: number
  pnlUsd: number
  winRate: number | null
}

export interface DayRecord {
  dayMs: number
  trips: number
  pnlUsd: number
}

export type FindingKind =
  | 'payoff'
  | 'side'
  | 'holdLeak'
  | 'holdBest'
  | 'sessionLeak'
  | 'sessionBest'
  | 'worstDay'
  | 'afterLoss'
  | 'fees'
  | 'instrumentLeak'
  | 'instrumentBest'

export interface Finding {
  kind: FindingKind
  /** 'stop' = a habit costing money; 'do' = what paid; 'note' = worth knowing. */
  tone: 'stop' | 'do' | 'note'
  /** Rough dollars at stake, for ordering. */
  weight: number
  params: Record<string, string | number>
}

export interface TradeJournal {
  trips: RoundTrip[]
  count: number
  wins: number
  winRate: number | null
  avgWinUsd: number | null
  /** A typical loss, as a positive number. */
  avgLossUsd: number | null
  /** Average win ÷ average loss. */
  payoff: number | null
  /** The payoff needed to break even at this win rate: (1 − w) ÷ w. */
  breakevenPayoff: number | null
  /** All winning dollars ÷ all losing dollars. */
  profitFactor: number | null
  /** Average dollars per trade. */
  expectancyUsd: number | null
  netPnlUsd: number
  grossPnlUsd: number
  feesUsd: number
  medianHoldMs: number | null
  bySide: JournalSlice<TradeSide>[]
  byHold: JournalSlice<HoldBucket>[]
  bySession: JournalSlice<Session>[]
  byInstrument: (JournalSlice & { exchange: Exchange })[]
  worstDay: DayRecord | null
  /** Trades opened within 30 minutes of a losing trade closing on the same account. */
  afterLoss: JournalSlice<'afterLoss'>
  findings: Finding[]
}

export interface JournalInput {
  trades: readonly Trade[]
  instrumentOf: (trade: Trade) => Instrument | undefined
  /**
   * The current open position (signed: + long, − short) on that account and instrument, in
   * the fills' units; 0 when flat.
   */
  currentPositionOf: (accountId: string, exchange: Exchange, symbol: string) => number
}

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY_MS = 86_400_000
const AFTER_LOSS_MS = 30 * MINUTE
/** A slice needs this many trades before it can carry a finding. */
const MIN_SLICE = 5

export const HOLD_BUCKETS: readonly HoldBucket[] = ['lt5m', 'm5to15', 'm15to60', 'h1to4', 'gt4h']
export const SESSIONS: readonly Session[] = ['asia', 'europe', 'us', 'late']

export function holdBucketOf(holdMs: number): HoldBucket {
  if (holdMs < 5 * MINUTE) return 'lt5m'
  if (holdMs < 15 * MINUTE) return 'm5to15'
  if (holdMs < HOUR) return 'm15to60'
  if (holdMs < 4 * HOUR) return 'h1to4'
  return 'gt4h'
}

export function sessionOf(ms: number): Session {
  const hour = new Date(ms).getUTCHours()
  if (hour < 8) return 'asia'
  if (hour < 13) return 'europe'
  if (hour < 21) return 'us'
  return 'late'
}

function num(value: string | undefined): number | null {
  if (value === undefined || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function isUsdQuoted(trade: Trade, instrument: Instrument | undefined): boolean {
  if (instrument?.quoteCurrency) return isUsdLikeCurrency(instrument.quoteCurrency)
  return /USD/i.test(trade.symbol) || /PERPETUAL$/i.test(trade.symbol)
}

interface OpenTrip {
  side: TradeSide
  openedAt: number
  /** Signed size in the fills' units. */
  position: number
  avgEntry: number
  maxAbs: number
  gross: number
  fees: number
  fills: number
  exitQty: number
  exitValue: number
}

/** Pairs fills into complete trades. Exported for tests. */
export function pairRoundTrips(input: JournalInput): RoundTrip[] {
  const groups = new Map<string, { trades: Trade[]; inverse: boolean }>()
  for (const trade of input.trades) {
    const instrument = input.instrumentOf(trade)
    if (instrument?.kind === InstrumentKind.INSTRUMENT_KIND_OPTION) continue
    const inverse = isInverseInstrument(trade.symbol, instrument)
    if (!inverse && !isUsdQuoted(trade, instrument)) continue
    const key = `${trade.accountId}|${trade.exchange}|${trade.symbol}`
    const group = groups.get(key) ?? { trades: [], inverse }
    group.trades.push(trade)
    groups.set(key, group)
  }

  const trips: RoundTrip[] = []
  for (const { trades, inverse } of groups.values()) {
    const fills = trades
      .map((trade) => ({
        trade,
        at: num(trade.executedAt) ?? 0,
        price: num(trade.price) ?? 0,
        signed:
          Math.abs(num(trade.quantity) ?? 0) * (trade.side === OrderSide.ORDER_SIDE_SELL ? -1 : 1),
        fee: feeUsd(trade) ?? 0,
      }))
      .filter((fill) => fill.price > 0 && fill.signed !== 0)
      .sort((a, b) => a.at - b.at || a.trade.id.localeCompare(b.trade.id))
    if (fills.length === 0) continue

    const first = fills[0].trade
    const net = fills.reduce((sum, fill) => sum + fill.signed, 0)
    const scale = Math.max(...fills.map((fill) => Math.abs(fill.signed)))
    const epsilon = scale * 1e-9
    const flat = (value: number) => Math.abs(value) <= epsilon

    // Position before the first fill in the window; a non-zero start means a trade we can't
    // price is already open, so follow its size without recording it.
    let position = input.currentPositionOf(first.accountId, first.exchange, first.symbol) - net
    if (flat(position)) position = 0
    let trip: OpenTrip | null = null

    // USD value of `qty` (fills' units) at `price`: inverse sizes are already USD.
    const usdOf = (qty: number, price: number) => (inverse ? qty : qty * price)
    const pnlOf = (side: TradeSide, qty: number, entry: number, exit: number) => {
      const direction = side === 'long' ? 1 : -1
      if (inverse) return direction * qty * (1 / entry - 1 / exit) * exit
      return direction * qty * (exit - entry)
    }
    const open = (fill: (typeof fills)[number], signed: number, fee: number): OpenTrip => ({
      side: signed > 0 ? 'long' : 'short',
      openedAt: fill.at,
      position: signed,
      avgEntry: fill.price,
      maxAbs: Math.abs(signed),
      gross: 0,
      fees: fee,
      fills: 1,
      exitQty: 0,
      exitValue: 0,
    })

    for (const fill of fills) {
      const { signed, price, at } = fill
      if (trip === null && position !== 0) {
        // Carried in from before the window.
        const next = position + signed
        if (flat(next) || Math.sign(next) !== Math.sign(position)) {
          const remainder = flat(next) ? 0 : next
          position = 0
          if (remainder !== 0) {
            trip = open(fill, remainder, fill.fee * (Math.abs(remainder) / Math.abs(signed)))
            position = remainder
          }
        } else {
          position = next
        }
        continue
      }
      if (trip === null) {
        trip = open(fill, signed, fill.fee)
        position = signed
        continue
      }
      if (Math.sign(signed) === Math.sign(trip.position)) {
        // Adding: average the entry (harmonically for inverse sizes).
        const total = Math.abs(trip.position) + Math.abs(signed)
        trip.avgEntry = inverse
          ? total / (Math.abs(trip.position) / trip.avgEntry + Math.abs(signed) / price)
          : (Math.abs(trip.position) * trip.avgEntry + Math.abs(signed) * price) / total
        trip.position += signed
        trip.maxAbs = Math.max(trip.maxAbs, Math.abs(trip.position))
        trip.fees += fill.fee
        trip.fills += 1
        position = trip.position
        continue
      }
      // Reducing, closing, or flipping.
      const closeQty = Math.min(Math.abs(signed), Math.abs(trip.position))
      const share = closeQty / Math.abs(signed)
      trip.gross += pnlOf(trip.side, closeQty, trip.avgEntry, price)
      trip.fees += fill.fee * share
      trip.fills += 1
      trip.exitQty += closeQty
      trip.exitValue += closeQty * price
      trip.position += Math.sign(signed) * closeQty
      if (flat(trip.position)) {
        const notional = usdOf(trip.maxAbs, trip.avgEntry)
        trips.push({
          id: `${first.accountId}:${first.exchange}:${first.symbol}:${trip.openedAt}`,
          accountId: first.accountId,
          exchange: first.exchange,
          symbol: first.symbol,
          side: trip.side,
          openedAt: trip.openedAt,
          closedAt: at,
          holdMs: Math.max(0, at - trip.openedAt),
          entryPrice: trip.avgEntry,
          exitPrice: trip.exitQty > 0 ? trip.exitValue / trip.exitQty : price,
          notionalUsd: notional,
          grossPnlUsd: trip.gross,
          feesUsd: trip.fees,
          pnlUsd: trip.gross - trip.fees,
          fills: trip.fills,
        })
        const remainder = Math.abs(signed) - closeQty
        if (remainder > epsilon) {
          const flipped = Math.sign(signed) * remainder
          trip = open(fill, flipped, fill.fee * (1 - share))
          position = flipped
        } else {
          trip = null
          position = 0
        }
      } else {
        position = trip.position
      }
    }
  }
  return trips.sort((a, b) => a.closedAt - b.closedAt)
}

function slice<K extends string>(key: K, trips: readonly RoundTrip[]): JournalSlice<K> {
  const pnl = trips.reduce((sum, trip) => sum + trip.pnlUsd, 0)
  const wins = trips.filter((trip) => trip.pnlUsd > 0).length
  return {
    key,
    trips: trips.length,
    pnlUsd: pnl,
    winRate: trips.length > 0 ? wins / trips.length : null,
  }
}

function median(values: number[]): number | null {
  if (values.length === 0) return null
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

function pct(value: number): number {
  return Math.round(value * 100)
}

export function buildJournal(input: JournalInput): TradeJournal {
  const trips = pairRoundTrips(input)
  const winners = trips.filter((trip) => trip.pnlUsd > 0)
  const losers = trips.filter((trip) => trip.pnlUsd < 0)
  const winSum = winners.reduce((sum, trip) => sum + trip.pnlUsd, 0)
  const lossSum = -losers.reduce((sum, trip) => sum + trip.pnlUsd, 0)
  const count = trips.length
  const winRate = count > 0 ? winners.length / count : null
  const avgWinUsd = winners.length > 0 ? winSum / winners.length : null
  const avgLossUsd = losers.length > 0 ? lossSum / losers.length : null
  const payoff = avgWinUsd !== null && avgLossUsd ? avgWinUsd / avgLossUsd : null
  const breakevenPayoff = winRate !== null && winRate > 0 ? (1 - winRate) / winRate : null
  const netPnlUsd = trips.reduce((sum, trip) => sum + trip.pnlUsd, 0)

  const bySide = (['long', 'short'] as const).map((side) =>
    slice(
      side,
      trips.filter((trip) => trip.side === side)
    )
  )
  const byHold = HOLD_BUCKETS.map((bucket) =>
    slice(
      bucket,
      trips.filter((trip) => holdBucketOf(trip.holdMs) === bucket)
    )
  )
  const bySession = SESSIONS.map((session) =>
    slice(
      session,
      trips.filter((trip) => sessionOf(trip.openedAt) === session)
    )
  )
  const instruments = new Map<string, RoundTrip[]>()
  for (const trip of trips) {
    const key = `${trip.exchange}|${trip.symbol}`
    instruments.set(key, [...(instruments.get(key) ?? []), trip])
  }
  const byInstrument = [...instruments.values()]
    .map((group) => ({ ...slice(group[0].symbol, group), exchange: group[0].exchange }))
    .sort((a, b) => b.trips - a.trips)

  const days = new Map<number, DayRecord>()
  for (const trip of trips) {
    const dayMs = Math.floor(trip.closedAt / DAY_MS) * DAY_MS
    const day = days.get(dayMs) ?? { dayMs, trips: 0, pnlUsd: 0 }
    day.trips += 1
    day.pnlUsd += trip.pnlUsd
    days.set(dayMs, day)
  }
  const worstDay =
    [...days.values()].filter((day) => day.pnlUsd < 0).sort((a, b) => a.pnlUsd - b.pnlUsd)[0] ??
    null

  // Trades opened soon after a loss closed on the same account ("getting it back").
  const lossesByAccount = new Map<string, number[]>()
  for (const trip of losers) {
    lossesByAccount.set(trip.accountId, [
      ...(lossesByAccount.get(trip.accountId) ?? []),
      trip.closedAt,
    ])
  }
  const afterLoss = slice(
    'afterLoss',
    trips.filter((trip) =>
      (lossesByAccount.get(trip.accountId) ?? []).some(
        (closedAt) => trip.openedAt >= closedAt && trip.openedAt - closedAt <= AFTER_LOSS_MS
      )
    )
  )

  const grossPnlUsd = trips.reduce((sum, trip) => sum + trip.grossPnlUsd, 0)
  const feesUsd = trips.reduce((sum, trip) => sum + trip.feesUsd, 0)
  const journal: TradeJournal = {
    trips,
    count,
    wins: winners.length,
    winRate,
    avgWinUsd,
    avgLossUsd,
    payoff,
    breakevenPayoff,
    profitFactor: lossSum > 0 ? winSum / lossSum : null,
    expectancyUsd: count > 0 ? netPnlUsd / count : null,
    netPnlUsd,
    grossPnlUsd,
    feesUsd,
    medianHoldMs: median(trips.map((trip) => trip.holdMs)),
    bySide,
    byHold,
    bySession,
    byInstrument,
    worstDay,
    afterLoss,
    findings: [],
  }
  journal.findings = findFindings(journal, lossSum)
  return journal
}

/** The habits worth naming, strongest first: at most four to stop and three that paid. */
export function findFindings(journal: TradeJournal, lossSum: number): Finding[] {
  if (journal.count < MIN_SLICE) return []
  const out: Finding[] = []
  const { winRate, payoff, breakevenPayoff, avgWinUsd, avgLossUsd } = journal

  if (
    winRate !== null &&
    payoff !== null &&
    breakevenPayoff !== null &&
    avgWinUsd !== null &&
    avgLossUsd !== null &&
    payoff < breakevenPayoff * 1.15
  ) {
    out.push({
      kind: 'payoff',
      tone: 'stop',
      weight: lossSum,
      params: {
        winRate: pct(winRate),
        avgWin: avgWinUsd,
        avgLoss: avgLossUsd,
        payoff: payoff.toFixed(2),
        needed: breakevenPayoff.toFixed(2),
      },
    })
  }

  const [long, short] = journal.bySide
  for (const [losing, other] of [
    [long, short],
    [short, long],
  ] as const) {
    if (losing.trips >= MIN_SLICE && losing.pnlUsd < 0 && other.pnlUsd > 0) {
      out.push({
        kind: 'side',
        tone: 'stop',
        weight: -losing.pnlUsd,
        params: { side: losing.key, pnl: losing.pnlUsd, trips: losing.trips },
      })
    }
  }

  const eligibleHold = journal.byHold.filter((s) => s.trips >= MIN_SLICE)
  const holdLeak = [...eligibleHold].sort((a, b) => a.pnlUsd - b.pnlUsd)[0]
  if (holdLeak && holdLeak.pnlUsd < 0) {
    out.push({
      kind: 'holdLeak',
      tone: 'stop',
      weight: -holdLeak.pnlUsd,
      params: { bucket: holdLeak.key, pnl: holdLeak.pnlUsd, trips: holdLeak.trips },
    })
  }
  const holdBest = [...eligibleHold].sort((a, b) => b.pnlUsd - a.pnlUsd)[0]
  if (holdBest && holdBest.pnlUsd > 0) {
    out.push({
      kind: 'holdBest',
      tone: 'do',
      weight: holdBest.pnlUsd,
      params: { bucket: holdBest.key, pnl: holdBest.pnlUsd, trips: holdBest.trips },
    })
  }

  const eligibleSession = journal.bySession.filter((s) => s.trips >= MIN_SLICE)
  const sessionLeak = [...eligibleSession].sort((a, b) => a.pnlUsd - b.pnlUsd)[0]
  if (sessionLeak && sessionLeak.pnlUsd < 0) {
    out.push({
      kind: 'sessionLeak',
      tone: 'stop',
      weight: -sessionLeak.pnlUsd,
      params: { session: sessionLeak.key, pnl: sessionLeak.pnlUsd, trips: sessionLeak.trips },
    })
  }
  const sessionBest = [...eligibleSession].sort((a, b) => b.pnlUsd - a.pnlUsd)[0]
  if (sessionBest && sessionBest.pnlUsd > 0) {
    out.push({
      kind: 'sessionBest',
      tone: 'do',
      weight: sessionBest.pnlUsd,
      params: { session: sessionBest.key, pnl: sessionBest.pnlUsd, trips: sessionBest.trips },
    })
  }

  // One day that wiped out a big share of all losses.
  if (journal.worstDay && lossSum > 0 && -journal.worstDay.pnlUsd >= lossSum * 0.25) {
    out.push({
      kind: 'worstDay',
      tone: 'stop',
      weight: -journal.worstDay.pnlUsd,
      params: {
        dayMs: journal.worstDay.dayMs,
        trips: journal.worstDay.trips,
        pnl: journal.worstDay.pnlUsd,
        share: pct(-journal.worstDay.pnlUsd / lossSum),
      },
    })
  }

  const { afterLoss } = journal
  if (
    afterLoss.trips >= MIN_SLICE &&
    afterLoss.pnlUsd < 0 &&
    afterLoss.winRate !== null &&
    winRate !== null &&
    afterLoss.winRate < winRate
  ) {
    out.push({
      kind: 'afterLoss',
      tone: 'stop',
      weight: -afterLoss.pnlUsd,
      params: {
        trips: afterLoss.trips,
        pnl: afterLoss.pnlUsd,
        winRate: pct(afterLoss.winRate),
        overall: pct(winRate),
      },
    })
  }

  if (journal.grossPnlUsd > 0 && journal.feesUsd >= journal.grossPnlUsd * 0.3) {
    out.push({
      kind: 'fees',
      tone: 'note',
      weight: journal.feesUsd,
      params: { fees: journal.feesUsd, share: pct(journal.feesUsd / journal.grossPnlUsd) },
    })
  }

  const eligibleInstrument = journal.byInstrument.filter((s) => s.trips >= MIN_SLICE)
  const instrumentLeak = [...eligibleInstrument].sort((a, b) => a.pnlUsd - b.pnlUsd)[0]
  if (instrumentLeak && instrumentLeak.pnlUsd < 0) {
    out.push({
      kind: 'instrumentLeak',
      tone: 'stop',
      weight: -instrumentLeak.pnlUsd,
      params: {
        symbol: instrumentLeak.key,
        pnl: instrumentLeak.pnlUsd,
        trips: instrumentLeak.trips,
      },
    })
  }
  const instrumentBest = [...eligibleInstrument].sort((a, b) => b.pnlUsd - a.pnlUsd)[0]
  if (instrumentBest && instrumentBest.pnlUsd > 0) {
    out.push({
      kind: 'instrumentBest',
      tone: 'do',
      weight: instrumentBest.pnlUsd,
      params: {
        symbol: instrumentBest.key,
        pnl: instrumentBest.pnlUsd,
        trips: instrumentBest.trips,
      },
    })
  }

  const byWeight = (a: Finding, b: Finding) => b.weight - a.weight
  return [
    ...out
      .filter((f) => f.tone === 'stop')
      .sort(byWeight)
      .slice(0, 4),
    ...out.filter((f) => f.tone === 'note'),
    ...out
      .filter((f) => f.tone === 'do')
      .sort(byWeight)
      .slice(0, 3),
  ]
}

/** How the month went, in one word, from the profit factor. */
export function verdictOf(journal: TradeJournal): 'paid' | 'flat' | 'lost' | 'tooFew' {
  if (journal.count < MIN_SLICE) return 'tooFew'
  const pf = journal.profitFactor
  if (pf === null) return journal.netPnlUsd >= 0 ? 'paid' : 'lost'
  if (pf >= 1.2) return 'paid'
  if (pf >= 0.95) return 'flat'
  return 'lost'
}
